import React, { useEffect, useState } from "react";
import {
  ListGroup,
  Row,
  Col,
  Image,
  FormControl,
  Button,
  Card,
} from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import Message from "../components/Message";
import Meta from "../components/Meta";
import { addToCart, removeFromCart } from "../redux/actions/cartActions";
import { listCategoryDiscounts } from "../redux/actions/categoryDiscountActions";

// FloatingPlaceOrder component
const FloatingPlaceOrder = ({
  subtotal,
  itemCount,
  discount,
  onCheckout,
  isExpanded,
  toggleExpand,
  cartItems,
  getCategoryDiscount,
  getDiscountedPrice,
}) => {
  return (
    <div
      className={`floating-placeorder ${isExpanded ? "expanded" : ""}`}
    >
      {/* Header Row always visible */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
        }}
      >
        <div>
          <span style={{ fontWeight: 500 }}>
            Rs. {Math.round(subtotal)} ・ {itemCount} items
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button
            size="sm"
            variant="primary"
            style={{ marginRight: 8 }}
            onClick={onCheckout}
          >
            PLACE ORDER
          </Button>
          <Button
            variant="link"
            onClick={toggleExpand}
            style={{ fontSize: 18 }}
            aria-label={isExpanded ? "Collapse order summary" : "Expand order summary"}
          >
            {isExpanded ? <FaChevronDown /> : <FaChevronUp />}
          </Button>
        </div>
      </div>

      {/* Content area: always rendered, height animated by class */}
      <div
        style={{
          borderTop: "1px solid #eee",
          background: "#fafbfc",
          padding: "16px",
        }}
      >
        {cartItems.map((item) => {
          const originalPrice = item.price.toFixed(2);
          const discountedPrice = getDiscountedPrice(item);
          const categoryDiscountPercent = getCategoryDiscount(item.category);
          const isDiscounted = categoryDiscountPercent > 0;

          return (
            <div
              key={item.product + "-" + item.size}
              style={{
                marginBottom: 12,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Item details */}
              <div>
                <strong>{item.name}</strong> ・ Size: {item.size}
              </div>
              <div>
                {item.qty} × Rs.{" "}
                {isDiscounted ? (
                  <>
                    <span
                      style={{
                        textDecoration: "line-through",
                        color: "#999",
                        marginRight: 6,
                      }}
                    >
                      {originalPrice}
                    </span>
                    <span>{discountedPrice}</span>
                  </>
                ) : (
                  <span>{originalPrice}</span>
                )}
                &nbsp; = Rs.{" "}
                {(item.qty * (isDiscounted ? discountedPrice : originalPrice)).toFixed(2)}
              </div>
            </div>
          );
        })}
        <hr />
        {/* Total Section */}
        <div
          style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}
        >
          <span>Total</span>
          <span>Rs. {Math.round(subtotal)}</span>
        </div>
      </div>
    </div>
  );
};

const CartScreen = () => {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.cartItems);
  const { discounts = [] } = useSelector((state) => state.categoryDiscountList);

  const [showSummary, setShowSummary] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    dispatch(listCategoryDiscounts());
  }, [dispatch]);

  // Detect mobile view on mount and resize
  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768);
    }

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getCategoryDiscount = (category) => {
    const d = discounts.find((x) => x.category === category);
    return d ? d.percent : 0;
  };

  const getDiscountedPrice = (item) => {
    const percent = getCategoryDiscount(item.category);
    if (percent) {
      return (item.price * (1 - percent / 100)).toFixed(2);
    }
    return item.price.toFixed(2);
  };

  const checkoutHandler = () => {
navigate("/shipping-payment", { state: { from: "cart" } });
  };

  const discountedSubtotal = cartItems
    .reduce((acc, item) => acc + item.qty * parseFloat(getDiscountedPrice(item)), 0)
    .toFixed(2);

  const originalSubtotal = cartItems
    .reduce((acc, item) => acc + item.qty * item.price, 0)
    .toFixed(2);

  const totalDiscount = (originalSubtotal - discountedSubtotal).toFixed(2);

  return (
    <>
      <Meta title="SYUKRA | Cart" />
      <Row>
        <h2 className="mb-3">Shopping Cart</h2>
        {cartItems.length === 0 ? (
          <Message variant="info">
            Your cart is empty <Link to="/">Go Back</Link>
          </Message>
        ) : (
          <Row>
            <Col md={8}>
              {cartItems.map((cart) =>
                isMobile ? (
                  <ListGroup variant="flush" key={cart.product + "-" + cart.size}>
                    

                      <ListGroup.Item
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          marginBottom: "10px",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                        }}
                      >
                        <Link to={`/product/${cart.product}`} state={{ from: "/cart" }}
>
                        <Image
                          src={cart.image}
                          style={{
                            width: "72px",       // width fixed
                            height: "108px",     // height set to 72 * 1.5 = 108px for 3:2 ratio (height:width)
                            objectFit: "cover",  // crops and fits the image nicely
                            borderRadius: "4px",
                            marginRight: "12px",
                          }}
                        />
                        </Link>

                        <div style={{ flex: 1 }}>
                          <Link to={`/product/${cart.product}`} state={{ from: "/cart" }}
>
                          <h6 style={{ marginBottom: 4 }}>
                            <div style={{ fontWeight: "400", fontSize: "1rem",color: "#2b2b2bff" }}>
                              {cart.name}
                            </div>
                          </h6>
                          <div style={{ fontSize: "0.85rem", color: "#2b2b2bff" }}>
                            Size: {cart.size}
                          </div>
                          <div style={{ marginTop: "6px" , color: "#2b2b2bff"}}>
                            Rs. {getCategoryDiscount(cart.category) ? (
                              <>
                                <span style={{ textDecoration: "line-through", color: "#999" }}>
                                  {cart.price}
                                </span>{" "}
                                <span style={{ color: "#2b2b2bff" }}>{getDiscountedPrice(cart)}</span>
                              </>
                            ) : (
                              Math.round(cart.price)
                            )}
                          </div>
                          </Link>
                        </div>
                        <div className="d-flex flex-column align-items-end" style={{ marginLeft: "12px" }}>
                          <div className="d-flex align-items-center mb-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              style={{ minWidth: 28 }}
                              onClick={() =>
                                dispatch(addToCart(cart.product, Math.max(cart.qty - 1, 1), cart.size))
                              }
                              disabled={cart.qty <= 1}
                            >
                              -
                            </Button>
                            <FormControl
                              value={cart.qty}
                              readOnly
                              plaintext
                              style={{
                                width: "40px",
                                textAlign: "center",
                                backgroundColor: "white",
                                userSelect: "none",
                                margin: "0 4px",
                              }}
                            />
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              style={{ minWidth: 28 }}
                              onClick={() =>
                                dispatch(
                                  addToCart(
                                    cart.product,
                                    Math.min(cart.qty + 1, Math.min(cart.countInStock, 10)),
                                    cart.size
                                  )
                                )
                              }
                              disabled={cart.qty >= Math.min(cart.countInStock, 10)}
                            >
                              +
                            </Button>
                          </div>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => dispatch(removeFromCart(cart.product, cart.size))}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      </ListGroup.Item>
                  </ListGroup>
                ) : (
                  // Original desktop rendering unchanged
                  <ListGroup variant="flush" key={cart.product + "-" + cart.size}>
                   
                      <ListGroup.Item>

                        <Row>
                          <Col md={2}>
                           <Link to={`/product/${cart.product}`} state={{ from: "/cart" }}
>
                            <Image src={cart.image} fluid rounded />
                            </Link>
                          </Col>
                          <Col style={{ marginLeft: "16px", paddingTop: "18px" }}>
                                                     <Link to={`/product/${cart.product}`} state={{ from: "/cart" }}
>
                            <h5>
                              <div style={{color:"#3e3e3eff"}}>{cart.name}</div>
                            </h5>
                            <div style={{color:"#3e3e3eff"}}>Size: <span>{cart.size}</span></div>
                            {getCategoryDiscount(cart.category) ? (
                              <>
                                <div>
                                  <span style={{ textDecoration: "line-through", color: "#999" }}>
                                    Rs. {cart.price}
                                  </span>
                                  <br />
                                  <span style={{ color: "#3e3e3eff", fontWeight: 400 }}>
                                    Rs. {getDiscountedPrice(cart)}
                                  </span>
                                  <br />
                                  <span
                                    style={{
                                      backgroundColor: "#cee0bdff",
                                      color: "#205c40ff",
                                      padding: "0 10px",
                                      borderRadius: "5px",
                                      fontSize: "0.8rem",
                                    }}
                                  >
                                    {getCategoryDiscount(cart.category)}% OFF
                                  </span>
                                </div>
                              </>
                            ) : (
                              <h6 className="py-2" style={{color: "#2b2b2bff" , fontSize:"16px"}}>Rs. {Math.round(cart.price)}</h6>
                            )}
                            </Link>
                          </Col>
                          <Col
                            className="d-flex flex-column align-items-end"
                            style={{ marginRight: "16px", paddingTop: "18px" }}
                          >
                            <div className="d-flex align-items-center">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                style={{ minWidth: 28 }}
                                onClick={() =>
                                  dispatch(
                                    addToCart(cart.product, Math.max(cart.qty - 1, 1), cart.size)
                                  )
                                }
                                disabled={cart.qty <= 1}
                                className="custom-qty-button"
                              >
                                -
                              </Button>
                              <FormControl
                                value={cart.qty}
                                readOnly
                                plaintext
                                style={{
                                  width: "40px",
                                  textAlign: "center",
                                  backgroundColor: "white",
                                  userSelect: "none",
                                  margin: "0 4px",
                                }}
                              />
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                style={{ minWidth: 28 }}
                                onClick={() =>
                                  dispatch(
                                    addToCart(
                                      cart.product,
                                      Math.min(cart.qty + 1, Math.min(cart.countInStock, 10)),
                                      cart.size
                                    )
                                  )
                                }
                                disabled={cart.qty >= Math.min(cart.countInStock, 10)}
                                className="custom-qty-button"
                              >
                                +
                              </Button>
                            </div>
                            <Button
                              className="my-3"
                              variant="danger"
                              onClick={() => dispatch(removeFromCart(cart.product, cart.size))}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </Col>
                        </Row>
                      </ListGroup.Item>
                  </ListGroup>
                )
              )}
            </Col>

            {/* Desktop place order summary */}
            <Col md={4} className="d-none d-md-block">
              <Card className="p-2 no-hover-card">
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <h4>
                      Subtotal (
                      {cartItems.reduce((acc, item) => acc + Number(item.qty), 0)}) Items
                    </h4>
                    {totalDiscount > 0 ? (
                      <>
                        <h5 className="mt-3">
                          <span
                            style={{
                              textDecoration: "line-through",
                              color: "#999",
                              Right: 8,
                            }}
                          >
                            Rs. {Math.round(originalSubtotal)}
                          </span>
                          <span style={{ color: "#3e3e3eff", fontWeight: 400 }}>
                            &nbsp;&nbsp;&nbsp;Rs. {Math.round(discountedSubtotal)}
                          </span>
                        </h5>
                        <span style={{ color: "#2c2c2cff", fontWeight: 400 }}>
                          You can save Rs.{Math.round(totalDiscount)} on this order!
                        </span>
                      </>
                    ) : (
                      <h5 className="mt-3">Rs. {Math.round(originalSubtotal)}</h5>
                    )}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Button
                      className="w-100 p-2"
                      variant="primary"
                      disabled={cartItems.length === 0}
                      onClick={checkoutHandler}
                    >
                      PROCEED TO CHECKOUT
                    </Button>
                  </ListGroup.Item>
                </ListGroup>
              </Card>
            </Col>
          </Row>
        )}
      </Row>

      {/* Floating Place Order only on mobile */}
      {isMobile && (
        <FloatingPlaceOrder
          subtotal={discountedSubtotal}
          itemCount={cartItems.reduce((acc, item) => acc + Number(item.qty), 0)}
          discount={totalDiscount}
          onCheckout={checkoutHandler}
          isExpanded={showSummary}
          toggleExpand={() => setShowSummary(!showSummary)}
          cartItems={cartItems}
          getCategoryDiscount={getCategoryDiscount}
          getDiscountedPrice={getDiscountedPrice}
        />
      )}
      {/* Padding so content isn't hidden behind fixed bar */}
      {isMobile && <div style={{ height: showSummary ? "auto" : "60px" }}></div>}
    </>
  );
};

export default CartScreen;
